import { useState } from "react";
import { View, Text } from "../base";
import { Icon } from "../base/Icon";
import { SpanDetailTabs, TabDefinition } from "./SpanDetailTabs";
import { RawSpanDetailView, RawSpanData } from "./RawSpanDetailView";
import { safeString } from "../../utils/strings";

export interface StopEventData {
  name?: string;
  data?: {
    closing_speech?: string | null;
    voice?: string | { provider?: string; identifier?: string } | null;
    speed?: number;
    language?: string;
  };
  timestamp?: string;
  turn?: number;
  background?: boolean;
  background_task_name?: string | null;
}

// Helper to format voice display - handles both string and object formats
const formatVoice = (voice: string | { provider?: string; identifier?: string } | null | undefined): string | null => {
  if (!voice) return null;
  if (typeof voice === "string") return voice;
  // Handle object format with provider and identifier
  const parts: string[] = [];
  if (voice.identifier) parts.push(voice.identifier);
  if (voice.provider) parts.push(`(${voice.provider})`);
  return parts.length > 0 ? parts.join(" ") : null;
};

const formatTimestamp = (timestamp: string): string => {
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      fractionalSecondDigits: 3,
    });
  } catch {
    return timestamp;
  }
};

// Content component for the Stop tab
const StopContent = ({ data }: { data: StopEventData }) => {
  return (
    <View className="space-y-4">
      {/* Stop Title */}
      <View>
        <View className="flex flex-row items-center gap-2">
          <Icon
            name="stop"
            size={20}
            className="text-red-500 dark:text-red-400"
          />
          <Text className="text-lg font-medium text-gray-800 dark:text-gray-200">Stop</Text>
        </View>
        {data.timestamp && <Text className="text-xs text-gray-400 mt-1 ml-7">{formatTimestamp(data.timestamp)}</Text>}
      </View>

      {/* Closing Speech */}
      {data.data?.closing_speech && (
        <View className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 border border-red-200 dark:border-red-800">
          <Text className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{safeString(data.data.closing_speech)}</Text>
        </View>
      )}

      {/* Settings - only show if closing speech exists */}
      {data.data?.closing_speech && (
        <View className="flex flex-row flex-wrap gap-4">
          {/* Voice */}
          {formatVoice(data.data?.voice) && (
            <View className="flex flex-row items-center gap-1.5">
              <Icon
                name="mic"
                size={14}
                className="text-gray-400"
              />
              <Text className="text-xs text-gray-600 dark:text-gray-400">
                Voice: <span className="font-medium text-gray-800 dark:text-gray-200">{formatVoice(data.data?.voice)}</span>
              </Text>
            </View>
          )}

          {/* Language */}
          {data.data?.language && (
            <View className="flex flex-row items-center gap-1.5">
              <Icon
                name="language"
                size={14}
                className="text-gray-400"
              />
              <Text className="text-xs text-gray-600 dark:text-gray-400">
                Language: <span className="font-medium text-gray-800 dark:text-gray-200">{safeString(data.data.language)}</span>
              </Text>
            </View>
          )}

          {/* Speed */}
          {data.data?.speed !== undefined && (
            <View className="flex flex-row items-center gap-1.5">
              <Icon
                name="activity"
                size={14}
                className="text-gray-400"
              />
              <Text className="text-xs text-gray-600 dark:text-gray-400">
                Speed: <span className="font-medium text-gray-800 dark:text-gray-200">{data.data.speed}x</span>
              </Text>
            </View>
          )}

          {/* Background */}
          {data.background && (
            <View className="flex flex-row items-center gap-1.5">
              <Icon
                name="layers"
                size={14}
                className="text-gray-400"
              />
              <Text className="text-xs text-gray-600 dark:text-gray-400">Background{data.background_task_name ? `: ${data.background_task_name}` : ""}</Text>
            </View>
          )}
        </View>
      )}
    </View>
  );
};

// Main StopEventSpanDetailView component with tabs
interface StopEventSpanDetailViewProps {
  data: StopEventData;
  rawData: RawSpanData;
  className?: string;
}

const tabs: TabDefinition[] = [
  { id: "stop", label: "Stop", color: "gray" },
  { id: "attributes", label: "Attributes", color: "gray" },
];

export const StopEventSpanDetailView = ({ data, rawData, className }: StopEventSpanDetailViewProps) => {
  const [activeTab, setActiveTab] = useState("stop");

  return (
    <View className={`flex flex-col overflow-hidden h-full ${className || ""}`}>
      <SpanDetailTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      <View className="flex-1 overflow-y-auto p-4">{activeTab === "stop" ? <StopContent data={data} /> : <RawSpanDetailView data={rawData} />}</View>
    </View>
  );
};

// Helper to parse stop event data from span attributes
export const parseStopEventData = (attributes: Record<string, unknown>): StopEventData | null => {
  const outputValue = attributes["output.value"];
  if (typeof outputValue !== "string") return null;

  try {
    const parsed = JSON.parse(outputValue);
    return parsed as StopEventData;
  } catch {
    return null;
  }
};
